/**
 * What each Claude call cost. Every call — reading a document, a review, a
 * question — reports its token counts here and they are appended to the
 * `Usage` tab, so the cost of the tool is measured, not estimated.
 * See ARCHITECTURE.md, Usage.
 */
import type Anthropic from '@anthropic-ai/sdk'
import { formatTimestamp } from './dates'
import type { Database, Row } from './google/sheets'

export type CallKind = 'extract' | 'review' | 'ask'

export interface CallUsage {
  kind: CallKind
  /** The file name or question the call was for. */
  ref: string
  /** The model that answered, which a server-side fallback can change. */
  model: string
  input_tokens: number
  cache_write_tokens: number
  cache_read_tokens: number
  /** Includes thinking. */
  output_tokens: number
  /** Web searches the call ran, billed per search on top of tokens. */
  web_searches?: number
  seconds: number
}

export type Meter = (usage: CallUsage) => void

/** US dollars per web search ($10 per 1,000), from Anthropic's price list. */
const SEARCH_PRICE = 0.01

/**
 * US dollars per million tokens, from Anthropic's published prices on
 * 2026-10-23. A cache write (5-minute TTL) is 1.25× input. Cache reads are
 * listed per model: $0.20 is 0.05× input on Opus 5.5 and 0.1× on Sonnet 5.5.
 * A model not listed here is recorded without a cost rather than with a guess.
 */
const PRICES: Record<string, { input: number; output: number; cacheRead: number }> = {
  'claude-opus-5-5': { input: 4, output: 20, cacheRead: 0.2 },
  'claude-sonnet-5-5': { input: 2, output: 10, cacheRead: 0.2 },
}

export function costOf(u: Omit<CallUsage, 'kind' | 'ref' | 'seconds'>): number | null {
  const p = PRICES[u.model.replace(/-\d{8}$/, '')]
  if (!p) return null
  const dollars =
    (u.input_tokens * p.input + u.cache_write_tokens * p.input * 1.25 + u.cache_read_tokens * p.cacheRead + u.output_tokens * p.output) / 1e6 +
    (u.web_searches ?? 0) * SEARCH_PRICE
  return Math.round(dollars * 10000) / 10000
}

type ApiUsage = Pick<Anthropic.Beta.BetaUsage, 'input_tokens' | 'output_tokens' | 'cache_creation_input_tokens' | 'cache_read_input_tokens'> & {
  server_tool_use?: { web_search_requests?: number } | null
}

/** Token counts from one or more API responses (a question's lookups are several). */
export function tokens(...usages: ApiUsage[]) {
  return usages.reduce(
    (t, u) => ({
      input_tokens: t.input_tokens + (u.input_tokens ?? 0),
      cache_write_tokens: t.cache_write_tokens + (u.cache_creation_input_tokens ?? 0),
      cache_read_tokens: t.cache_read_tokens + (u.cache_read_input_tokens ?? 0),
      output_tokens: t.output_tokens + (u.output_tokens ?? 0),
      web_searches: t.web_searches + (u.server_tool_use?.web_search_requests ?? 0),
    }),
    { input_tokens: 0, cache_write_tokens: 0, cache_read_tokens: 0, output_tokens: 0, web_searches: 0 },
  )
}

export function toRow(u: CallUsage, at = new Date()): Row {
  return {
    at: formatTimestamp(at),
    kind: u.kind,
    ref: u.ref.slice(0, 120),
    model: u.model,
    input_tokens: u.input_tokens,
    cache_write_tokens: u.cache_write_tokens,
    cache_read_tokens: u.cache_read_tokens,
    output_tokens: u.output_tokens,
    web_searches: u.web_searches ?? 0,
    cost_usd: costOf(u),
    seconds: Math.round(u.seconds * 10) / 10,
  }
}

/** A meter that appends to the Usage tab; a failed write never fails the call it measured. */
export function sheetMeter(db: Database): Meter {
  return (u) => {
    db.append('Usage', [toRow(u)]).catch(() => undefined)
  }
}

export interface UsageSummary {
  month: string
  total: number
  calls: number
  byKind: { kind: CallKind; cost: number; calls: number; seconds: number }[]
}

/** Spend in one month (`YYYY-MM`), by kind of call. */
export function summarise(rows: Row[], month: string): UsageSummary {
  const inMonth = rows.filter((r) => String(r.at ?? '').startsWith(month))
  const kinds: CallKind[] = ['extract', 'review', 'ask']
  const byKind = kinds
    .map((kind) => {
      const list = inMonth.filter((r) => r.kind === kind)
      return {
        kind,
        cost: list.reduce((s, r) => s + (Number(r.cost_usd) || 0), 0),
        calls: list.length,
        seconds: list.reduce((s, r) => s + (Number(r.seconds) || 0), 0),
      }
    })
    .filter((k) => k.calls > 0)
  return { month, total: byKind.reduce((s, k) => s + k.cost, 0), calls: inMonth.length, byKind }
}
